import { useCallback, useEffect, useState } from 'react';
import {
  BadgeCheck,
  CircleCheck,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminPaymentStats,
  AdminTransaction,
  adminPaymentsApi,
} from '../services/paymentApi';
import { PAYMENT_METHOD_META } from '../config/defaults';
import { PaymentMethod } from '../types';
import { formatPrice } from '../components/PlanCard';

/** Libellé d'un moyen de paiement, tolérant aux anciennes valeurs stockées. */
function methodLabel(method: string): string {
  const meta = PAYMENT_METHOD_META[method as PaymentMethod];
  if (meta) return meta.label;
  // Transactions antérieures au modèle multi-canal.
  if (method === 'mobile_push') return 'Mobile';
  if (method === 'card') return 'Carte bancaire';
  return method;
}

/**
 * ─── Admin · Transactions de paiement ────────────────────────────────────────
 *
 * Registre serveur de TOUTES les tentatives de paiement. Cas d'usage clé :
 * un utilisateur a été débité mais son abonnement n'est pas actif (agrégateur
 * qui coupe, check du statut en retard / jamais abouti). L'admin retrouve la
 * transaction ici puis :
 *   - « Vérifier (API) »  : re-check auprès de l'agrégateur — si confirmé,
 *     l'abonnement est activé et la facture envoyée automatiquement ;
 *   - « Valider manuellement » : succès forcé — abonnement + facture ;
 *   - « Renvoyer la facture » : pour une transaction déjà réussie.
 */

const STATUS_META: Record<string, { label: string; cls: string }> = {
  success: { label: 'Réussie', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  verifying: { label: 'Vérification', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  failed: { label: 'Échouée', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { label: 'Annulée', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
  expired: { label: 'Expirée', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  network_error: { label: 'Erreur réseau', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const VERIFIED_BY_LABEL: Record<string, string> = {
  client: 'auto',
  api: 'via API',
  manual: 'manuelle',
};

function StatusChip({ tx }: { tx: AdminTransaction }) {
  const meta = STATUS_META[tx.status] ?? {
    label: tx.status,
    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  return (
    <div className="flex flex-col items-start gap-1">
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
        {meta.label}
      </span>
      {tx.status === 'success' && tx.verifiedBy && (
        <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
          <BadgeCheck size={11} /> {VERIFIED_BY_LABEL[tx.verifiedBy] ?? tx.verifiedBy}
        </span>
      )}
    </div>
  );
}

/** Statuts "à régler" : la transaction n'a pas abouti côté client. */
function isActionable(status: string): boolean {
  return status !== 'success';
}

export function PaymentTransactionsPage() {
  const [items, setItems] = useState<AdminTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<AdminPaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  /** id de la transaction dont une action est en cours (verrouille ses boutons). */
  const [busyId, setBusyId] = useState<string | null>(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [txRes, statsRes] = await Promise.all([
        adminPaymentsApi.listTransactions({
          page,
          limit,
          status: statusFilter || undefined,
          search: search || undefined,
        }),
        adminPaymentsApi.getStats(30).catch(() => null),
      ]);
      setItems(txRes.data.items ?? []);
      setTotal(txRes.data.total ?? 0);
      if (statsRes) setStats(statsRes.data);
    } catch {
      toast.error('Erreur lors du chargement des transactions');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const replaceRow = (tx: AdminTransaction) =>
    setItems((prev) => prev.map((t) => (t.id === tx.id ? tx : t)));

  const handleCheck = async (tx: AdminTransaction) => {
    setBusyId(tx.id);
    try {
      const { data } = await adminPaymentsApi.checkTransaction(tx.id);
      replaceRow(data.transaction);
      if (data.outcome === 'success') toast.success(data.message);
      else if (data.outcome === 'failed') toast.error(data.message);
      else toast.info(data.message);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleValidate = async (tx: AdminTransaction) => {
    if (
      !confirm(
        `Valider MANUELLEMENT le paiement de ${tx.userEmail} (${formatPrice(tx.amount, tx.currency)}, réf. ${tx.reference}) ?\n\nSon abonnement sera activé et la facture envoyée par email.`,
      )
    )
      return;
    setBusyId(tx.id);
    try {
      const { data } = await adminPaymentsApi.validateTransaction(tx.id);
      replaceRow(data.transaction);
      toast.success(data.message);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleResendInvoice = async (tx: AdminTransaction) => {
    setBusyId(tx.id);
    try {
      const { data } = await adminPaymentsApi.resendInvoice(tx.id);
      replaceRow(data.transaction);
      toast.success(data.message);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Transactions de paiement</h1>
        <p className="text-gray-500 dark:text-gray-400">
          {total} transaction{total !== 1 ? 's' : ''} — re-check API ou validation manuelle des paiements coupés
        </p>
      </div>

      {/* Résumé 30 jours */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Transactions (30j)" value={String(stats.totals.total)} />
          <SummaryCard label="Réussies" value={String(stats.totals.success)} accent="text-emerald-600 dark:text-emerald-400" />
          <SummaryCard label="En attente" value={String(stats.totals.pending)} accent="text-amber-600 dark:text-amber-400" />
          <SummaryCard label="Échouées" value={String(stats.totals.failed)} accent="text-red-600 dark:text-red-400" />
          <SummaryCard
            label="Revenus (30j)"
            value={[
              stats.totals.revenueUSD > 0 ? formatPrice(stats.totals.revenueUSD, 'USD') : null,
              stats.totals.revenueCDF > 0 ? formatPrice(stats.totals.revenueCDF, 'CDF') : null,
            ].filter(Boolean).join(' + ') || '—'}
            accent="text-emerald-600 dark:text-emerald-400"
          />
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Email, référence, n° d'ordre provider..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="verifying">Vérification</option>
          <option value="success">Réussies</option>
          <option value="failed">Échouées</option>
          <option value="cancelled">Annulées</option>
          <option value="expired">Expirées</option>
          <option value="network_error">Erreur réseau</option>
        </select>
        <button
          onClick={load}
          className="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 hover:text-emerald-600 transition-colors"
          title="Actualiser"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Wallet className="w-10 h-10 mb-3 opacity-50" />
            <p className="text-base font-medium">Aucune transaction</p>
            <p className="text-xs mt-1">Les paiements des utilisateurs apparaîtront ici automatiquement.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan / Montant</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((tx) => {
                    const busy = busyId === tx.id;
                    return (
                      <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                          <span className="block text-[10px] text-gray-400">
                            {new Date(tx.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{tx.userName}</p>
                          <p className="text-xs text-gray-400">{tx.userEmail}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900 dark:text-white">{tx.planName}</p>
                          <p className="text-xs font-semibold text-gray-500">
                            {formatPrice(tx.amount, tx.currency)}
                            <span className="font-normal text-gray-400"> · {tx.providerId} · {methodLabel(tx.method)}</span>
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-mono text-gray-600 dark:text-gray-300">{tx.reference}</p>
                          {tx.providerOrderNumber && (
                            <p className="text-[10px] font-mono text-gray-400" title="N° d'ordre provider">
                              ↳ {tx.providerOrderNumber}
                            </p>
                          )}
                          {tx.failureReason && (
                            <p className="text-[10px] text-red-400 max-w-[200px] truncate" title={tx.failureReason}>
                              {tx.failureReason}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3"><StatusChip tx={tx} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {busy ? (
                              <Loader2 size={16} className="animate-spin text-emerald-500" />
                            ) : isActionable(tx.status) ? (
                              <>
                                <button
                                  onClick={() => handleCheck(tx)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                                  title="Re-vérifier le statut réel auprès de l'API de l'agrégateur"
                                >
                                  <RefreshCw size={13} /> Vérifier (API)
                                </button>
                                <button
                                  onClick={() => handleValidate(tx)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors"
                                  title="Valider manuellement : abonnement activé + facture envoyée"
                                >
                                  <ShieldCheck size={13} /> Valider
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleResendInvoice(tx)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                                title={tx.invoiceSentAt ? `Facture envoyée le ${new Date(tx.invoiceSentAt).toLocaleDateString('fr-FR')}` : 'Renvoyer la facture par email'}
                              >
                                <Mail size={13} /> Renvoyer la facture
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500">Page {page} sur {totalPages} · {total} au total</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <p className="flex items-start gap-2 text-xs text-gray-400">
        <CircleCheck size={14} className="mt-0.5 shrink-0" />
        « Vérifier (API) » interroge l'agrégateur (FlexPay check) : si le paiement est confirmé,
        l'abonnement est activé et la facture envoyée automatiquement. « Valider » force le succès
        sans vérification API — à utiliser quand l'utilisateur a bien été débité mais que le check
        ne passe pas.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
      <p className={`text-xl font-bold ${accent ?? 'text-gray-900 dark:text-white'}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

function extractError(err: unknown): string {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ||
    (err as Error)?.message ||
    'Une erreur est survenue';
  return Array.isArray(msg) ? msg.join(', ') : msg;
}
